import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import { authService } from '../../services/api';

const Settings = () => {
  const navigate = useNavigate();
  const currentUser = authService.getCurrentUser();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const settingsSections = [
    {
      title: 'Gerenciamento de Usuários',
      description: 'Gerencie usuários, permissões e acessos ao sistema',
      icon: 'Users',
      path: '/user-management',
      color: 'bg-blue-500',
      availableFor: ['ADMIN']
    },
    {
      title: 'Configurações do Hotel',
      description: 'Configure informações básicas do hotel',
      icon: 'Building2',
      path: '/hotel-settings',
      color: 'bg-green-500',
      availableFor: ['ADMIN', 'MANAGER']
    },
    {
      title: 'Tipos de Quarto',
      description: 'Gerencie os tipos de quarto disponíveis no sistema',
      icon: 'Tags',
      path: '/room-types',
      color: 'bg-pink-500',
      availableFor: ['ADMIN']
    },
    {
      title: 'Perguntas da Pesquisa',
      description: 'Gerencie as perguntas da pesquisa de satisfação',
      icon: 'MessageSquare',
      path: '/survey-templates',
      color: 'bg-amber-500',
      availableFor: ['ADMIN', 'MANAGER']
    },
    {
      title: 'Preferências do Sistema',
      description: 'Configure notificações, idioma e outras preferências',
      icon: 'Settings',
      path: '/system-preferences',
      color: 'bg-purple-500',
      availableFor: ['ADMIN', 'MANAGER', 'STAFF']
    },
    {
      title: 'Backup e Restauração',
      description: 'Gerencie backups e restaurações do sistema',
      icon: 'Database',
      path: '/backup-restore',
      color: 'bg-orange-500',
      availableFor: ['ADMIN']
    },
    {
      title: 'Logs do Sistema',
      description: 'Visualize logs de atividades e auditoria',
      icon: 'FileText',
      path: '/system-logs',
      color: 'bg-gray-500',
      availableFor: ['ADMIN']
    },
    {
      title: 'Integrações',
      description: 'Configure integrações com sistemas externos',
      icon: 'Link',
      path: '/integrations',
      color: 'bg-indigo-500',
      availableFor: ['ADMIN', 'MANAGER']
    }
  ];

  const filteredSections = settingsSections.filter(section => 
    section.availableFor.includes(currentUser?.role)
  );

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuClick={() => setIsMobileMenuOpen(true)} />
      <Sidebar isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      
      <div className="lg:ml-60 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-foreground">Configurações</h1>
              <p className="text-muted-foreground mt-2">
                Gerencie todas as configurações do sistema
              </p>
            </div>

            {/* Settings Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSections.map((section, index) => (
                <div
                  key={index}
                  className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                  onClick={() => {
                    if (section.path === '/user-management' || section.path === '/hotel-settings' || section.path === '/room-types' || section.path === '/survey-templates') {
                      navigate(section.path);
                    } else {
                      // Para outras páginas que ainda não foram implementadas
                      alert(`${section.title} será implementada em breve!`);
                    }
                  }}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`w-12 h-12 ${section.color} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200`}>
                      <Icon name={section.icon} size={24} color="white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors duration-200">
                        {section.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {section.description}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      Disponível para: {section.availableFor.join(', ')}
                    </span>
                    <Icon 
                      name="ArrowRight" 
                      size={16} 
                      color="var(--color-muted-foreground)" 
                      className="group-hover:translate-x-1 transition-transform duration-200"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="mt-12 bg-card border border-border rounded-lg p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">Ações Rápidas</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                  onClick={() => alert('Exportar configurações será implementado em breve!')}
                >
                  Exportar Configurações
                </Button>
                <Button
                  variant="outline"
                  iconName="Upload"
                  iconPosition="left"
                  onClick={() => alert('Importar configurações será implementado em breve!')}
                >
                  Importar Configurações
                </Button>
                <Button
                  variant="outline"
                  iconName="RotateCcw"
                  iconPosition="left"
                  onClick={() => {
                    if (confirm('Tem certeza que deseja redefinir todas as configurações para os valores padrão?')) {
                      alert('Redefinir configurações será implementado em breve!');
                    }
                  }}
                >
                  Redefinir Padrões
                </Button>
              </div>
            </div>

            {/* System Information */}
            <div className="mt-8 bg-card border border-border rounded-lg p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4">Informações do Sistema</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-sm text-muted-foreground">Versão do Sistema</div>
                  <div className="text-lg font-semibold text-foreground">1.0.0</div>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-sm text-muted-foreground">Última Atualização</div>
                  <div className="text-lg font-semibold text-foreground">Hoje</div>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-sm text-muted-foreground">Status</div>
                  <div className="text-lg font-semibold text-green-600">Online</div>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-sm text-muted-foreground">Usuário Atual</div>
                  <div className="text-lg font-semibold text-foreground">{currentUser?.role || 'N/A'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
